import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { fr } from 'date-fns/locale';
import { Alert, Box } from '@mui/material';
import axios from 'axios';

interface CustomDatePickerProps {
    label: string;
    value: Date | null;
    onChange: (date: Date | null) => void;
    productId?: string;
    minDate?: Date;
    fullWidth?: boolean;
    required?: boolean;
    mode?: 'slots' | 'reservations';
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
    label,
    value,
    onChange,
    productId,
    minDate = new Date(),
    fullWidth = true,
    required = false,
    mode = 'slots'
}) => {
    const [datesWithData, setDatesWithData] = useState<string[]>([]);

    const fetchDatesWithData = async () => {
        if (!productId) return;

        try {
            const endpoint = mode === 'slots'
                ? `/api/planning/dates-with-slots?productId=${productId}`
                : `/api/planning/dates-with-reservations?productId=${productId}`;

            const response = await axios.get(endpoint);
            setDatesWithData(response.data);
        } catch (error) {
            console.error(`Erreur lors de la récupération des dates avec ${mode}:`, error);
        }
    };

    useEffect(() => {
        if (productId) {
            fetchDatesWithData();
        }
    }, [productId, mode]);

    const shouldDisableDate = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) {
            return true;
        }
        return false;
    };

    const isDateWithData = (date: Date): boolean => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        return datesWithData.includes(dateStr);
    };

    const CustomDay = (props: PickersDayProps<Date>) => {
        const { day, outsideCurrentMonth, ...other } = props;
        
        const hasData = !outsideCurrentMonth && isDateWithData(day);

        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                sx={{
                    ...(hasData && {
                        backgroundColor: '#4caf50 !important',
                        color: 'white !important',
                        fontWeight: 'bold',
                        '&:hover': {
                            backgroundColor: '#45a049 !important',
                        },
                        '&.Mui-selected': {
                            backgroundColor: '#2e7d32 !important',
                        },
                    }),
                }}
            />
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <Box>
                <DatePicker
                    label={label}
                    value={value}
                    onChange={onChange}
                    minDate={minDate}
                    shouldDisableDate={shouldDisableDate}
                    slots={{
                        day: CustomDay
                    }}
                    slotProps={{
                        textField: {
                            fullWidth,
                            required
                        }
                    }}
                    format="dd/MM/yyyy"
                />
                {datesWithData.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        <strong>Légende :</strong> Les dates avec un fond vert indiquent qu'il existe des {mode === 'slots' ? 'créneaux disponibles' : 'réservations'}.
                    </Alert>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default CustomDatePicker;
